import { getStyleDeclaration } from './get-style-declaration.js';
import {
  activeState,
  disabledFocusState,
  disabledState,
  hoverState,
  relativeBorderRadius,
  widgetSelector,
} from './helpers/index.js';
import { px2em } from '#/providers/creative/px2em/@impl/px2em.js';
import { INumberPickerWidget } from 'types/widgets.js';
import { getWidgetGeometry } from '../recalculate-positions.js';

export function numberPickerWidget(widgetDescription: INumberPickerWidget) {
  const {
    placements = [],
    buttonStylesStates,
    textColor,
    backgroundColor,
    internalSizes,
    width,
    height,
    direction,
  } = widgetDescription;
  const selector = widgetSelector(widgetDescription);
  const rules = [
    ...placements.map(getStyleDeclaration.positions(selector)),
    ...placements.map(getStyleDeclaration.size(selector)),
  ];

  const horizontalDirection = direction === 'horizontal';

  const borderRadiusValue = horizontalDirection ? height / 2 : width / 2;

  rules.push([
    selector,
    [
      ['color', textColor],
      ['background', backgroundColor],
      [
        'border-radius',
        relativeBorderRadius(borderRadiusValue, { width, height }),
      ],
    ],
  ]);

  const buttonSelector = `${selector} button`;
  const buttonGeometry = getWidgetGeometry(
    {
      width: internalSizes.button.size,
      height: internalSizes.button.size,
    },
    width,
    height,
  );

  rules.push([
    buttonSelector,
    [
      ['margin', `${(internalSizes.button.margin * 100) / width}%`],
      ['height', buttonGeometry.relativeSize.height],
      ['width', buttonGeometry.relativeSize.width],
      ['aspect-ratio', '1/1'],
      ['position', 'relative'],
    ],
  ]);

  const iconSelector = `${selector} svg`;
  const iconGeometry = getWidgetGeometry(
    {
      width: internalSizes.button.iconSize,
      height: internalSizes.button.iconSize,
    },
    internalSizes.button.size,
    internalSizes.button.size,
  );
  const iconHeight = horizontalDirection
    ? iconGeometry.relativeSize.height
    : iconGeometry.relativeSize.width;

  rules.push([
    iconSelector,
    [
      ['height', iconHeight],
      ['width', 'initial'],
      ['aspect-ratio', '1/1'],
    ],
  ]);

  const counterSelector = `${selector} [data-role='number-picker-counter']`;

  rules.push([
    counterSelector,
    [
      ['font-size', px2em(internalSizes.counter.fontSize)],
      ['color', textColor],
    ],
  ]);

  if (direction === 'vertical') {
    rules.push([counterSelector, [['width', '100%']]]);
  }

  if (buttonStylesStates) {
    const {
      default: base,
      hover,
      click: active,
      disabled,
      disabledFocus,
    } = buttonStylesStates;

    rules.push([
      buttonSelector,
      [
        ['background', base.backgroundColor],
        ['fill', base.iconColor],
      ],
    ]);

    rules.push([
      hoverState(buttonSelector),
      [
        ['background', hover.backgroundColor],
        ['fill', hover.iconColor],
      ],
    ]);

    rules.push([
      activeState(buttonSelector),
      [
        ['background', active.backgroundColor],
        ['fill', active.iconColor],
      ],
    ]);

    rules.push([
      disabledState(buttonSelector),
      [
        ['background', disabled.backgroundColor],
        ['fill', disabled.iconColor],
      ],
    ]);

    rules.push([
      disabledFocusState(buttonSelector),
      [
        ['background', disabledFocus.backgroundColor],
        ['fill', disabledFocus.iconColor],
      ],
    ]);
  }

  return rules;
}
